import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Stack,
  Alert,
  Loader,
  Select,
  Box,
  Grid,
  Divider,
  NumberInput,
  Card,
  Tabs,
  Modal,
  Table,
  ScrollArea,
  Center,
  Checkbox
} from '@mantine/core';
import { 
  IconSearch, 
  IconUser, 
  IconUsers, 
  IconBuildingStore,
  IconCalendar,
  IconCalendarEvent,
  IconHome,
  IconCurrencyDollar,
  IconPlus,
  IconEdit,
  IconTrash,
  IconX,
  IconCheck,
  IconReceipt,
  IconCreditCard
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../utils/constants';

function ReservationScreen({ reservationType = 'paying-guest' }) {
  // Screen state
  const [currentScreen, setCurrentScreen] = useState('reservation'); // 'reservation', 'guest-info', or 'confirmation'
  
  // Account states
  const [guestAccount, setGuestAccount] = useState(null);
  const [groupAccount, setGroupAccount] = useState(null);
  const [travelAgentAccount, setTravelAgentAccount] = useState(null);
  
  // Reservation details
  const [arrivalDate, setArrivalDate] = useState(new Date());
  const [nights, setNights] = useState(1);
  const [departureDate, setDepartureDate] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  
  // Unit and rate selection
  const [selectedUnitType, setSelectedUnitType] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  
  // Rate summary
  const [rateSummary, setRateSummary] = useState({
    baseRate: 0,
    taxes: 0,
    otherCharges: 0,
    securityDeposit: 0,
    total: 0
  });
  
  // Rate quotes
  const [rateQuotes, setRateQuotes] = useState([]);
  
  // Guest form state
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
    phone: '',
    phone1: '',
    phone2: '',
    email: '',
    market: '',
    source: '',
    creditCardNumber: '',
    expirationDate: '',
    csv: '',
    notes: ''
  });
  
  // Loading states
  const [loading, setLoading] = useState(false);
  
  // Confirmation screen state
  const [createdReservation, setCreatedReservation] = useState(null);
  
  // Search modal states
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchType, setSearchType] = useState('guest'); // 'guest', 'group', 'agent'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Unit selection modal state
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  
  // Rate Plan selection modal state
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [ratePlans, setRatePlans] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  
  // Rate grid state
  const [rateGrids, setRateGrids] = useState({}); // Store grids for all rates
  const [rateGridLoading, setRateGridLoading] = useState(false);
  const [reservationDates, setReservationDates] = useState([]);
  
  // Reservation type configurations
  const reservationTypeConfigs = {
    'paying-guest': {
      title: 'Paying Guest Reservation',
      defaultRate: 150,
      defaultMarketCode: 'LEISURE',
      defaultSource: 'DIRECT',
      defaultReservationType: 'PAYING_GUEST',
      showGroup: false,
      showTravelAgent: false
    },
    'owner': {
      title: 'Owner Reservation',
      defaultRate: 0,
      defaultMarketCode: 'OWNER',
      defaultSource: 'OWNER_PORTAL',
      defaultReservationType: 'OWNER',
      showGroup: false,
      showTravelAgent: false
    },
    'group': {
      title: 'Group Reservation',
      defaultRate: 120,
      defaultMarketCode: 'GROUP',
      defaultSource: 'GROUP_SALES',
      defaultReservationType: 'GROUP',
      showGroup: true,
      showTravelAgent: true
    },
    'timeshare': {
      title: 'Timeshare Reservation',
      defaultRate: 0,
      defaultMarketCode: 'TIMESHARE',
      defaultSource: 'TIMESHARE_EXCHANGE',
      defaultReservationType: 'TIMESHARE',
      showGroup: false,
      showTravelAgent: false
    }
  };

  const currentConfig = reservationTypeConfigs[reservationType] || reservationTypeConfigs['paying-guest'];

  // Unit types state - fetched from database
  const [unitTypes, setUnitTypes] = useState([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(true);
  
  // Tax configuration state
  const [taxConfig, setTaxConfig] = useState(null);
  const [loadingTaxConfig, setLoadingTaxConfig] = useState(false);

  // Refs to prevent infinite loops in date calculations
  const isUpdatingFromNights = useRef(false);
  const isUpdatingFromDeparture = useRef(false);

  // Sample units data (for fallback/display purposes)
  const sampleUnits = [
    { id: 1, number: 'A101', type: '1-bedroom', status: 'available', rate: 150 },
    { id: 2, number: 'A102', type: '1-bedroom', status: 'available', rate: 150 },
    { id: 3, number: 'B201', type: '2-bedroom', status: 'available', rate: 200 },
    { id: 4, number: 'B202', type: '2-bedroom', status: 'maintenance', rate: 200 },
    { id: 5, number: 'C301', type: '3-bedroom', status: 'available', rate: 250 }
  ];


  // Update departure date when nights change
  useEffect(() => {
    if (!isUpdatingFromDeparture.current) {
      isUpdatingFromNights.current = true;
      const newDepartureDate = new Date(arrivalDate);
      newDepartureDate.setDate(newDepartureDate.getDate() + nights);
      setDepartureDate(newDepartureDate);
      // Reset the flag after a brief delay to allow state to update
      setTimeout(() => {
        isUpdatingFromNights.current = false;
      }, 0);
    }
  }, [arrivalDate, nights]);

  // Update nights when departure date changes
  useEffect(() => {
    if (!isUpdatingFromNights.current) {
      isUpdatingFromDeparture.current = true;
      const timeDiff = departureDate.getTime() - arrivalDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      if (daysDiff > 0) {
        setNights(daysDiff);
      }
      // Reset the flag after a brief delay to allow state to update
      setTimeout(() => {
        isUpdatingFromDeparture.current = false;
      }, 0);
    }
  }, [departureDate, arrivalDate]);

  // Update rate summary when selections change
  useEffect(() => {
    let baseRate = 0;
    
    // If a rate is selected, calculate total from rate grid data
    if (selectedRate && rateGrids[selectedRate.id] && rateGrids[selectedRate.id].length > 0) {
      baseRate = rateGrids[selectedRate.id].reduce((sum, day) => sum + day.rate, 0);
    } else {
      // Fallback to unit rate only (no hardcoded rates)
      baseRate = selectedUnit?.rate || 0;
    }
    
    // Calculate taxes based on tax configuration
    const taxes = calculateTaxes(baseRate, taxConfig);
    const otherCharges = 0; // Other charges
    const securityDeposit = 0; // Security deposit
    const total = baseRate + taxes + otherCharges + securityDeposit;

    setRateSummary({
      baseRate,
      taxes,
      otherCharges,
      securityDeposit,
      total
    });
  }, [selectedUnit, selectedUnitType, unitTypes, selectedRate, rateGrids, taxConfig]);

  // Fetch unit types from database
  useEffect(() => {
    fetchUnitTypes();
  }, []);

  // Fetch tax configuration when unit type changes
  useEffect(() => {
    if (selectedUnitType) {
      fetchTaxConfig(selectedUnitType);
    } else {
      setTaxConfig(null);
    }
  }, [selectedUnitType]);

  const fetchUnitTypes = async () => {
    try {
      setLoadingUnitTypes(true);
      const response = await fetch(`${API_URL}/unit-types`);
      if (!response.ok) throw new Error('Failed to fetch unit types');
      
      const data = await response.json();
      // Transform the data to match the expected format
      const transformedData = data.map(unitType => ({
        value: unitType.id.toString(),
        label: unitType.unit_type
      }));
      setUnitTypes(transformedData);
    } catch (error) {
      console.error('Error fetching unit types:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load unit types',
        color: 'red',
      });
    } finally {
      setLoadingUnitTypes(false);
    }
  };

  // Fetch tax configuration based on unit type's default tax code
  const fetchTaxConfig = async (unitTypeId) => {
    try {
      setLoadingTaxConfig(true);
      
      // Get unit type details to find default tax code
      const unitTypeResponse = await fetch(`${API_URL}/unit-types/${unitTypeId}`);
      if (!unitTypeResponse.ok) {
        throw new Error('Failed to fetch unit type details');
      }
      const unitType = await unitTypeResponse.json();
      
      let taxCode = unitType.default_tax_code;
      
      // If no tax code on unit type, get the tax with lowest sort order
      if (!taxCode) {
        const token = localStorage.getItem('token');
        const taxesResponse = await fetch(`${API_URL}/taxes?sortBy=sort_order&sortOrder=ASC`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!taxesResponse.ok) {
          throw new Error('Failed to fetch taxes');
        }
        const taxes = await taxesResponse.json();
        if (taxes.length > 0) {
          taxCode = taxes[0].tax_code;
        }
      }
      
      // Fetch the specific tax configuration
      if (taxCode) {
        const token = localStorage.getItem('token');
        const taxResponse = await fetch(`${API_URL}/taxes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!taxResponse.ok) {
          throw new Error('Failed to fetch tax configurations');
        }
        const allTaxes = await taxResponse.json();
        const tax = allTaxes.find(t => t.tax_code === taxCode);
        
        if (tax) {
          setTaxConfig(tax);
        } else {
          setTaxConfig(null);
        }
      } else {
        setTaxConfig(null);
      }
    } catch (error) {
      console.error('Error fetching tax config:', error);
      setTaxConfig(null);
    } finally {
      setLoadingTaxConfig(false);
    }
  };

  // Calculate taxes based on tax configuration and base rate
  const calculateTaxes = (baseRate, taxConfig) => {
    if (!taxConfig || !baseRate) return 0;
    
    let totalTax = 0;
    
    // Calculate tax for each tax field (tax1 through tax6)
    for (let i = 1; i <= 6; i++) {
      const taxRate = taxConfig[`tax${i}`];
      if (taxRate && taxRate > 0) {
        const taxAmount = (baseRate * taxRate) / 100;
        totalTax += Math.round(taxAmount * 100) / 100; // Round to 2 decimal places
      }
    }
    
    return Math.round(totalTax * 100) / 100; // Final round to 2 decimal places
  };

  // Calculate total tax percentage from tax configuration
  const calculateTaxPercentage = (taxConfig) => {
    if (!taxConfig) return 0;
    
    let totalPercentage = 0;
    
    // Sum up all tax percentages (tax1 through tax6)
    for (let i = 1; i <= 6; i++) {
      const taxRate = taxConfig[`tax${i}`];
      if (taxRate && taxRate > 0) {
        totalPercentage += taxRate;
      }
    }
    
    return Math.round(totalPercentage * 100) / 100; // Round to 2 decimal places
  };

  // API search functions
  const searchGuests = async (searchQuery) => {
    try {
      setSearchLoading(true);
      const response = await fetch(`${API_URL}/guests/search/${encodeURIComponent(searchQuery)}?limit=20`);
      if (response.ok) {
        const results = await response.json();
        return results;
      } else {
        throw new Error('Failed to search guests');
      }
    } catch (error) {
      console.error('Error searching guests:', error);
      notifications.show({
        title: 'Search Error',
        message: 'Failed to search guests. Please try again.',
        color: 'red'
      });
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  const searchGroups = async (searchQuery) => {
    try {
      setSearchLoading(true);
      const response = await fetch(`${API_URL}/groups/search/${encodeURIComponent(searchQuery)}?limit=20`);
      if (response.ok) {
        const results = await response.json();
        return results;
      } else {
        throw new Error('Failed to search groups');
      }
    } catch (error) {
      console.error('Error searching groups:', error);
      notifications.show({
        title: 'Search Error',
        message: 'Failed to search groups. Please try again.',
        color: 'red'
      });
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  const searchAgents = async (searchQuery) => {
    try {
      setSearchLoading(true);
      const response = await fetch(`${API_URL}/agents/search/${encodeURIComponent(searchQuery)}?limit=20`);
      if (response.ok) {
        const results = await response.json();
        return results;
      } else {
        throw new Error('Failed to search agents');
      }
    } catch (error) {
      console.error('Error searching agents:', error);
      notifications.show({
        title: 'Search Error',
        message: 'Failed to search agents. Please try again.',
        color: 'red'
      });
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchAvailableUnits = async () => {
    if (!arrivalDate || !departureDate) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please select arrival and departure dates first',
        color: 'orange',
      });
      return;
    }

    setUnitsLoading(true);
    try {
      const params = new URLSearchParams({
        arrivalDate: arrivalDate.toISOString().split('T')[0],
        departureDate: departureDate.toISOString().split('T')[0],
      });
      
      if (selectedUnitType) {
        params.append('unitTypeId', selectedUnitType);
      }

      const url = `${API_URL}/units/available?${params}`;
      console.log('Fetching available units from:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Available units data:', data);
      setAvailableUnits(data.units);
      setUnitModalOpen(true);
    } catch (error) {
      console.error('Error fetching available units:', error);
      notifications.show({
        title: 'Error',
        message: `Failed to fetch available units: ${error.message}`,
        color: 'red',
      });
    } finally {
      setUnitsLoading(false);
    }
  };

  const fetchRatePlans = async () => {
    setRatesLoading(true);
    try {
      const response = await fetch(`${API_URL}/rates`);
      if (!response.ok) throw new Error('Failed to fetch rate plans');
      
      const data = await response.json();
      setRatePlans(data);
      
      // Auto-fetch rate grids for all active rate plans if unit type is selected
      if (data.length > 0 && selectedUnitType) {
        const activeRates = data.filter(rate => rate.active);
        activeRates.forEach(rate => {
          fetchRateGridData(rate, selectedUnitType);
        });
      }
    } catch (error) {
      console.error('Error fetching rate plans:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch rate plans',
        color: 'red',
      });
    } finally {
      setRatesLoading(false);
    }
  };

  // Generate reservation dates array
  const generateReservationDates = () => {
    const dates = [];
    const startDate = new Date(arrivalDate);
    
    for (let i = 0; i < nights; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    setReservationDates(dates);
    return dates;
  };

  // Fetch BAR rates for the selected rate plan and date range
  const fetchRateGridData = async (ratePlan, unitTypeId) => {
    if (!ratePlan || !unitTypeId) return;
    
    setRateGridLoading(true);
    try {
      const dates = generateReservationDates();
      const startDate = dates[0];
      const endDate = dates[dates.length - 1];
      
      console.log('Fetching rate grid data:', {
        bar_header_id: ratePlan.bar_rate_id,
        unit_type_id: unitTypeId,
        start_date: startDate,
        end_date: endDate
      });
      
      // Fetch BAR rates for the date range and unit type
      const response = await fetch(`${API_URL}/bar-rates?bar_header_id=${ratePlan.bar_rate_id}&unit_type_id=${unitTypeId}&start_date=${startDate}&end_date=${endDate}`);
      
      console.log('API Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`Failed to fetch rate grid data: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Rate grid data received:', data);
      
      // Organize data by date
      const rateDataByDate = {};
      data.forEach(rate => {
        // Convert database date to YYYY-MM-DD format for matching
        const dateOnly = new Date(rate.date).toISOString().split('T')[0];
        rateDataByDate[dateOnly] = parseFloat(rate.rate);
      });
      
      // Create grid data structure
      const gridData = dates.map(date => ({
        date,
        rate: rateDataByDate[date] || 0,
        formattedDate: new Date(date).toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        })
      }));
      
      setRateGrids(prev => ({
        ...prev,
        [ratePlan.id]: gridData
      }));
    } catch (error) {
      console.error('Error fetching rate grid data:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch rate information',
        color: 'red',
      });
    } finally {
      setRateGridLoading(false);
    }
  };

  const handleAccountSearch = (accountType) => {
    setSearchType(accountType);
    setSearchTerm('');
    setSearchResults([]);
    setSearchModalOpen(true);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      notifications.show({
        title: 'Search Required',
        message: 'Please enter a search term',
        color: 'yellow'
      });
      return;
    }

    let results = [];
    switch (searchType) {
      case 'guest':
        results = await searchGuests(searchTerm);
        break;
      case 'group':
        results = await searchGroups(searchTerm);
        break;
      case 'agent':
        results = await searchAgents(searchTerm);
        break;
      default:
        break;
    }
    
    setSearchResults(results);
  };

  const handleSelectAccount = (account) => {
    switch (searchType) {
      case 'guest':
        setGuestAccount(account);
        
        // Auto-populate guest form with selected guest data
        setGuestForm({
          firstName: account.firstname || '',
          lastName: account.lastname || '',
          address1: account.address1 || '',
          address2: account.address2 || '',
          city: account.city || '',
          state: account.state || '',
          zipcode: account.zipcode || '',
          country: account.country || 'US',
          phone: account.phone1 || '',
          phone1: account.phone1 || '',
          phone2: account.phone2 || '',
          email: account.email || '',
          market: account.market || '',
          source: account.source || '',
          creditCardNumber: '',
          expirationDate: '',
          csv: '',
          notes: account.notes || ''
        });
        break;
      case 'group':
        setGroupAccount(account);
        break;
      case 'agent':
        setTravelAgentAccount(account);
        break;
      default:
        break;
    }
    
    setSearchModalOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    
    notifications.show({
      title: 'Account Selected',
      message: `${account.firstname || account.name || account.agency_name} selected`,
      color: 'green'
    });
  };

  const handleClearAccount = (accountType) => {
    switch (accountType) {
      case 'guest':
        setGuestAccount(null);
        break;
      case 'group':
        setGroupAccount(null);
        break;
      case 'agent':
        setTravelAgentAccount(null);
        break;
      default:
        break;
    }
  };

  const handleSelectUnit = (unit) => {
    setSelectedUnit(unit);
    setUnitModalOpen(false);
    notifications.show({
      title: 'Unit Selected',
      message: `Selected unit ${unit.unit_number}`,
      color: 'green',
    });
  };

  const handleRateSelection = () => {
    fetchRatePlans();
    setRateModalOpen(true);
  };

  const handleSelectRate = async (ratePlan) => {
    setSelectedRate(ratePlan);
    setRateModalOpen(false);
    
    // Fetch tax configuration if unit type is selected
    if (selectedUnitType) {
      await fetchTaxConfig(selectedUnitType);
    }
    
    // Auto-generate rate quote
    if (rateGrids[ratePlan.id] && rateGrids[ratePlan.id].length > 0) {
      const baseRate = rateGrids[ratePlan.id].reduce((sum, day) => sum + day.rate, 0);
      
      // Calculate taxes based on tax configuration
      const calculatedTaxes = calculateTaxes(baseRate, taxConfig);
      
      const otherCharges = 0;
      const securityDeposit = 0;
      const total = baseRate + calculatedTaxes + otherCharges + securityDeposit;
      
      const newQuote = {
        id: Date.now(), // Simple ID generation
        ratePlanId: ratePlan.id,
        name: ratePlan.title,
        description: `${arrivalDate.toLocaleDateString('en-US', { weekday: 'short' })} ${arrivalDate.toLocaleDateString()} ${nights} Nights ${departureDate.toLocaleDateString('en-US', { weekday: 'short' })} ${departureDate.toLocaleDateString()} ${selectedUnitType && unitTypes.length > 0 ? unitTypes.find(ut => ut.value === selectedUnitType)?.label : 'No Unit Type'} Unit ${selectedUnit ? selectedUnit.unit_number : 'No Unit'}`,
        baseRate: baseRate,
        taxes: calculatedTaxes,
        otherCharges: otherCharges,
        securityDeposit: securityDeposit,
        total: total,
        createdAt: new Date()
      };
      
      // Add new quote at the beginning (most recent first)
      setRateQuotes(prev => [newQuote, ...prev]);
    }
    
    notifications.show({
      title: 'Rate Plan Selected',
      message: `Selected rate plan ${ratePlan.title}`,
      color: 'green',
    });
  };

  const handleSelectQuote = (quote) => {
    // Find the rate plan for this quote
    const ratePlan = ratePlans.find(rp => rp.id === quote.ratePlanId);
    if (ratePlan) {
      setSelectedRate(ratePlan);
      notifications.show({
        title: 'Rate Quote Selected',
        message: `Reverted to ${quote.name}`,
        color: 'blue',
      });
    }
  };

  const handleMakeReservation = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!guestForm.firstName || !guestForm.lastName) {
        notifications.show({
          title: 'Validation Error',
          message: 'First name and last name are required',
          color: 'red'
        });
        return;
      }

      if (!selectedUnit || !selectedRate || !arrivalDate || !departureDate) {
        notifications.show({
          title: 'Validation Error',
          message: 'Unit, rate, arrival date, and departure date are required',
          color: 'red'
        });
        return;
      }

      const token = localStorage.getItem('token');
      
      // Prepare reservation data
      const reservationData = {
        // Guest information
        first_name: guestForm.firstName,
        last_name: guestForm.lastName,
        address1: guestForm.address1,
        address2: guestForm.address2,
        city: guestForm.city,
        state: guestForm.state,
        zipcode: guestForm.zipcode,
        country: guestForm.country,
        phone1: guestForm.phone1 || guestForm.phone,
        phone2: guestForm.phone2,
        email: guestForm.email,
        
        // Reservation details
        arrival_date: arrivalDate.toISOString().split('T')[0],
        departure_date: departureDate.toISOString().split('T')[0],
        nights: nights,
        people1: adults,
        people2: children,
        
        // Unit and rate information
        unit_id: selectedUnit.id,
        unit_type_id: selectedUnitType,
        rate_plan_id: selectedRate.id,
        
        // Pricing
        base_rate: rateQuotes.length > 0 ? rateQuotes[0].baseRate : 0,
        taxes: rateQuotes.length > 0 ? rateQuotes[0].taxes : 0,
        other_charges: rateQuotes.length > 0 ? rateQuotes[0].otherCharges : 0,
        security_deposit: rateQuotes.length > 0 ? rateQuotes[0].securityDeposit : 0,
        total_amount: rateQuotes.length > 0 ? rateQuotes[0].total : 0,
        
        // Account information
        guest_id: guestAccount?.id || null,
        group_id: groupAccount?.id || null,
        travel_agent_id: travelAgentAccount?.id || null,
        
        // Business information
        market_code: guestForm.market || currentConfig.defaultMarketCode,
        source: guestForm.source || currentConfig.defaultSource,
        reservation_type: currentConfig.defaultReservationType,
        
        // Payment information
        credit_card_number: guestForm.creditCardNumber,
        expiration_date: guestForm.expirationDate,
        csv: guestForm.csv,
        
        // Notes
        notes: guestForm.notes,
        
        // Status
        status: 'CONFIRMED',
        created_at: new Date().toISOString()
      };

      // Save reservation to database
      const response = await fetch(`${API_URL}/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reservationData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create reservation');
      }

      const savedReservation = await response.json();
      
      // Show success notification
      notifications.show({
        title: 'Reservation Created',
        message: `Reservation ${savedReservation.id} has been created successfully`,
        color: 'green'
      });
      
      // Set the created reservation and show confirmation screen
      setCreatedReservation(savedReservation);
      setCurrentScreen('confirmation');
      
    } catch (error) {
      console.error('Error creating reservation:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create reservation',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalConfirmation = () => {
    // Reset all form data
    setGuestAccount(null);
    setGroupAccount(null);
    setTravelAgentAccount(null);
    setGuestForm({
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
      country: 'US',
      phone: '',
      phone1: '',
      phone2: '',
      email: '',
      market: '',
      source: '',
      creditCardNumber: '',
      expirationDate: '',
      csv: '',
      notes: ''
    });
    setRateQuotes([]);
    setSelectedUnit(null);
    setSelectedRate(null);
    setSelectedUnitType('');
    setCreatedReservation(null);
    
    // Navigate back to front-desk-list
    window.location.href = '/dashboard/front-desk-list';
  };

  const handleUnitTypeChange = (value) => {
    setSelectedUnitType(value);
    setSelectedUnit(null); // Clear selected unit when type changes
  };

  const handleUnitSelection = () => {
    fetchAvailableUnits();
  };

  const handleGenerateQuotes = () => {
    notifications.show({
      title: 'Rate Quotes',
      message: 'Generating rate quotes...',
      color: 'green'
    });
    // TODO: Implement rate quote generation
  };

  return (
    <Container fluid p="md" style={{ height: '100vh', overflow: 'auto' }}>
      {currentScreen === 'reservation' ? (
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>{currentConfig.title}</Title>
          <Badge color="blue" size="lg">
            {reservationType.toUpperCase().replace('-', ' ')}
          </Badge>
        </Group>

        {/* Account Section */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Grid>
            <Grid.Col span={4}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '100px' }}>Guest Account</Text>
                  <TextInput
                    placeholder="Search guest account..."
                    value={guestAccount ? `${guestAccount.firstname || ''} ${guestAccount.lastname || ''}`.trim() : ''}
                    leftSection={<IconUser size="1rem" />}
                    style={{ flex: 1 }}
                    readOnly
                  />
                  <Button
                    variant="outline"
                    leftSection={<IconSearch size="1rem" />}
                    onClick={() => handleAccountSearch('guest')}
                  >
                    Find
                  </Button>
                  {guestAccount && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleClearAccount('guest')}
                    >
                      <IconX size="1rem" />
                    </ActionIcon>
                  )}
                </Group>
              </Stack>
            </Grid.Col>

            <Grid.Col span={4}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '100px' }}>Group Account</Text>
                  <TextInput
                    placeholder="Search group account..."
                    value={groupAccount ? groupAccount.name : ''}
                    leftSection={<IconUsers size="1rem" />}
                    style={{ flex: 1 }}
                    readOnly
                  />
                  <Button
                    variant="outline"
                    leftSection={<IconSearch size="1rem" />}
                    onClick={() => handleAccountSearch('group')}
                  >
                    Find
                  </Button>
                  {groupAccount && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleClearAccount('group')}
                    >
                      <IconX size="1rem" />
                    </ActionIcon>
                  )}
                </Group>
                {groupAccount && (
                  <Text size="xs" c="dimmed">
                    {groupAccount.contact} • {groupAccount.phone}
                  </Text>
                )}
              </Stack>
            </Grid.Col>

            <Grid.Col span={4}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '100px' }}>Travel Agent</Text>
                  <TextInput
                    placeholder="Search travel agent..."
                    value={travelAgentAccount ? travelAgentAccount.agency_name : ''}
                    leftSection={<IconBuildingStore size="1rem" />}
                    style={{ flex: 1 }}
                    readOnly
                  />
                  <Button
                    variant="outline"
                    leftSection={<IconSearch size="1rem" />}
                    onClick={() => handleAccountSearch('agent')}
                  >
                    Find
                  </Button>
                  {travelAgentAccount && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleClearAccount('agent')}
                    >
                      <IconX size="1rem" />
                    </ActionIcon>
                  )}
                </Group>
                {travelAgentAccount && (
                  <Text size="xs" c="dimmed">
                    {travelAgentAccount.agency_name} • {travelAgentAccount.phone1}
                  </Text>
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Dates and People Section */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Grid>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '90px' }}>Arrival Date</Text>
                  <TextInput
                    type="date"
                    value={arrivalDate.toISOString().split('T')[0]}
                    onChange={(e) => setArrivalDate(new Date(e.target.value))}
                    leftSection={<IconCalendar size="1rem" />}
                    placeholder="Select arrival date"
                    style={{ flex: 1 }}
                  />
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={2}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '50px' }}>Nights</Text>
                  <NumberInput
                    value={nights}
                    onChange={(value) => setNights(value || 1)}
                    min={1}
                    max={365}
                    style={{ flex: 1 }}
                  />
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '100px' }}>Departure Date</Text>
                  <TextInput
                    type="date"
                    value={departureDate.toISOString().split('T')[0]}
                    onChange={(e) => setDepartureDate(new Date(e.target.value))}
                    leftSection={<IconCalendarEvent size="1rem" />}
                    placeholder="Select departure date"
                    style={{ flex: 1 }}
                  />
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={2}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '50px' }}>Adults</Text>
                  <NumberInput
                    value={adults}
                    onChange={(value) => setAdults(value || 1)}
                    min={1}
                    max={20}
                    style={{ flex: 1 }}
                  />
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={2}>
              <Stack gap="xs">
                <Group>
                  <Text size="sm" fw={500} style={{ minWidth: '60px' }}>Children</Text>
                  <NumberInput
                    value={children}
                    onChange={(value) => setChildren(value || 0)}
                    min={0}
                    max={20}
                    style={{ flex: 1 }}
                  />
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Unit Selection and Rate Summary */}
        <Grid>
          {/* Unit Selection - Right 2/3 */}
          <Grid.Col span={8}>
            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Group>
                      <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Unit Type</Text>
                      <Select
                        placeholder={loadingUnitTypes ? "Loading..." : "Select unit type"}
                        value={selectedUnitType}
                        onChange={handleUnitTypeChange}
                        data={unitTypes}
                        leftSection={<IconHome size="1rem" />}
                        style={{ flex: 1 }}
                        disabled={loadingUnitTypes}
                      />
                    </Group>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Group>
                      <Text size="sm" fw={500} style={{ minWidth: '40px' }}>Unit</Text>
                      <Button
                        variant="outline"
                        leftSection={<IconHome size="1rem" />}
                        onClick={handleUnitSelection}
                        disabled={!selectedUnitType}
                        style={{ flex: 1 }}
                      >
                        {selectedUnit ? selectedUnit.unit_number : 'Select Unit'}
                      </Button>
                    </Group>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Group>
                      <Text size="sm" fw={500} style={{ minWidth: '40px' }}>Rate</Text>
                      <Button
                        variant="outline"
                        leftSection={<IconCurrencyDollar size="1rem" />}
                        onClick={handleRateSelection}
                        style={{ flex: 1 }}
                      >
                        {selectedRate ? selectedRate.title : 'Select Rate'}
                      </Button>
                    </Group>
                  </Stack>
                </Grid.Col>
              </Grid>
              
              <Divider my="md" />
              <Group gap="md">
                <Button
                  variant="outline"
                  leftSection={<IconCalendar size="1rem" />}
                  onClick={() => {
                    // TODO: Implement Tape Chart functionality
                    notifications.show({
                      title: 'Tape Chart',
                      message: 'Tape Chart functionality will be implemented soon',
                      color: 'blue',
                    });
                  }}
                >
                  Tape Chart
                </Button>
                <Button
                  variant="outline"
                  leftSection={<IconReceipt size="1rem" />}
                  onClick={() => {
                    // TODO: Implement Folio functionality
                    notifications.show({
                      title: 'Folio',
                      message: 'Folio functionality will be implemented soon',
                      color: 'blue',
                    });
                  }}
                >
                  Folio
                </Button>
                <Button
                  variant="outline"
                  leftSection={<IconCreditCard size="1rem" />}
                  onClick={() => {
                    // TODO: Implement Post Charge functionality
                    notifications.show({
                      title: 'Post Charge',
                      message: 'Post Charge functionality will be implemented soon',
                      color: 'blue',
                    });
                  }}
                >
                  Post Charge
                </Button>
              </Group>

              {/* Rate Quotes Section */}
              <Divider my="md" />
              <Title order={4} mb="sm">Rate Quotes</Title>
              
              {rateQuotes.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No rate quotes available. Select a rate to generate your first quote.
                </Text>
              ) : (
                <Stack gap="sm">
                  {rateQuotes.map((quote, index) => (
                    <Card key={quote.id} withBorder>
                      <Group justify="space-between" align="center">
                        <Group gap="lg">
                          <Text fw={500} size="sm">{quote.name}</Text>
                          <Text size="sm" c="dimmed">{quote.description}</Text>
                          <Group gap="xs">
                            <Text size="sm">Base: ${quote.baseRate.toFixed(2)}</Text>
                            <Text size="sm">Tax: ${quote.taxes.toFixed(2)}</Text>
                            <Text size="sm">Other: ${quote.otherCharges.toFixed(2)}</Text>
                            <Text size="sm">Sec: ${quote.securityDeposit.toFixed(2)}</Text>
                          </Group>
                          <Text fw={700} c="blue" size="lg">${quote.total.toFixed(2)}</Text>
                        </Group>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectQuote(quote)}
                        >
                          Select
                        </Button>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid.Col>

          {/* Rate Summary - Left 1/3 */}
          <Grid.Col span={4}>
            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Title order={3} mb="md">Rate Summary</Title>
              {selectedRate && (
                <Text size="sm" c="dimmed" mb="xs">
                  Rate Plan: {selectedRate.title}
                </Text>
              )}
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Base Rate:</Text>
                  <Text size="sm" fw={500}>${rateSummary.baseRate.toFixed(2)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Other Charges:</Text>
                  <Text size="sm" fw={500}>${rateSummary.otherCharges.toFixed(2)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Taxes ({taxConfig ? calculateTaxPercentage(taxConfig) : 0}%):</Text>
                  <Text size="sm" fw={500}>${rateSummary.taxes.toFixed(2)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Security Deposit:</Text>
                  <Text size="sm" fw={500}>${rateSummary.securityDeposit.toFixed(2)}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="lg" fw={700}>Total:</Text>
                  <Text size="lg" fw={700} c="blue">${rateSummary.total.toFixed(2)}</Text>
                </Group>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Make Reservation Button */}
        <Group justify="flex-end" mt="xl">
          <Button
            size="lg"
            onClick={() => setCurrentScreen('guest-info')}
            disabled={!selectedRate || !selectedUnit}
          >
            Make Reservation
          </Button>
        </Group>
        </Stack>
      ) : currentScreen === 'guest-info' ? (
        /* Guest Information Screen */
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Title order={1}>Guest Information</Title>
            <Group gap="sm">
              <Button
                variant="outline"
                color="red"
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel this reservation?')) {
                    // Reset all form data and go back to first screen
                    setGuestAccount(null);
                    setGroupAccount(null);
                    setTravelAgentAccount(null);
                    setGuestForm({
                      firstName: '',
                      lastName: '',
                      address1: '',
                      address2: '',
                      city: '',
                      state: '',
                      zipcode: '',
                      country: 'US',
                      phone: '',
                      phone1: '',
                      phone2: '',
                      email: '',
                      market: '',
                      source: '',
                      creditCardNumber: '',
                      expirationDate: '',
                      csv: '',
                      notes: ''
                    });
                    setRateQuotes([]);
                    setCurrentScreen('reservation');
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentScreen('reservation')}
              >
                Back to Reservation
              </Button>
              <Button
                onClick={handleMakeReservation}
                disabled={!guestForm.firstName || !guestForm.lastName}
              >
                Complete Reservation
              </Button>
            </Group>
          </Group>

          {/* Account Section */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
            <Title order={3} mb="md">Account Selection</Title>
            <Grid>
              <Grid.Col span={4}>
                <Stack gap="xs">
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>Guest</Text>
                    <TextInput
                      placeholder="No guest selected"
                      value={guestAccount ? `${guestAccount.firstname || ''} ${guestAccount.lastname || ''}`.trim() : ''}
                      leftSection={<IconUser size="1rem" />}
                      style={{ flex: 1 }}
                      readOnly
                    />
            <Button
              variant="outline"
                      leftSection={<IconSearch size="1rem" />}
                      onClick={() => {
                        setSearchType('guest');
                        setSearchModalOpen(true);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                    >
                      Search
            </Button>
                    {guestAccount && (
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => setGuestAccount(null)}
                        size="sm"
                      >
                        <IconX size="1rem" />
                      </ActionIcon>
                    )}
          </Group>
                </Stack>
              </Grid.Col>

              <Grid.Col span={4}>
                    <Stack gap="xs">
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>Group</Text>
                    <TextInput
                      placeholder="No group selected"
                      value={groupAccount ? groupAccount.group_name : ''}
                      leftSection={<IconUsers size="1rem" />}
                      style={{ flex: 1 }}
                      readOnly
                    />
                    <Button
                      variant="outline"
                      leftSection={<IconSearch size="1rem" />}
                      onClick={() => {
                        setSearchType('group');
                        setSearchModalOpen(true);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                    >
                      Search
                    </Button>
                    {groupAccount && (
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => setGroupAccount(null)}
                        size="sm"
                      >
                        <IconX size="1rem" />
                      </ActionIcon>
                    )}
                  </Group>
                    </Stack>
              </Grid.Col>

              <Grid.Col span={4}>
                <Stack gap="xs">
                    <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>Agent</Text>
                    <TextInput
                      placeholder="No agent selected"
                      value={travelAgentAccount ? travelAgentAccount.agency_name : ''}
                      leftSection={<IconBuildingStore size="1rem" />}
                      style={{ flex: 1 }}
                      readOnly
                    />
                    <Button
                      variant="outline"
                      leftSection={<IconSearch size="1rem" />}
                      onClick={() => {
                        setSearchType('agent');
                        setSearchModalOpen(true);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                    >
                      Search
                    </Button>
                    {travelAgentAccount && (
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => setTravelAgentAccount(null)}
                        size="sm"
                      >
                        <IconX size="1rem" />
                      </ActionIcon>
                    )}
                    </Group>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Guest Information Form */}
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Title order={3} mb="md">Guest Information</Title>
            <Grid>
              {/* Left Half */}
              <Grid.Col span={6}>
                <Stack gap="md">
                  {/* Row 1: First Name, Last Name */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>First Name *</Text>
                    <TextInput
                      value={guestForm.firstName}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, firstName: e.target.value }))}
                      style={{ flex: 1 }}
                      required
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Last Name *</Text>
                    <TextInput
                      value={guestForm.lastName}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, lastName: e.target.value }))}
                      style={{ flex: 1 }}
                      required
                    />
                  </Group>

                  {/* Row 2: Address1 */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Address</Text>
                    <TextInput
                      value={guestForm.address1}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, address1: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </Group>

                  {/* Row 3: Address2 */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Address 2</Text>
                    <TextInput
                      value={guestForm.address2}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, address2: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </Group>

                  {/* Row 4: City, State, Zip */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>City</Text>
                    <TextInput
                      value={guestForm.city}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, city: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>State</Text>
                    <TextInput
                      value={guestForm.state}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, state: e.target.value }))}
                      style={{ width: '100px' }}
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>Zip</Text>
                    <TextInput
                      value={guestForm.zipcode}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, zipcode: e.target.value }))}
                      style={{ width: '100px' }}
                    />
                  </Group>

                  {/* Row 5: Country, Phone */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Country</Text>
                    <Select
                      value={guestForm.country}
                      onChange={(value) => setGuestForm(prev => ({ ...prev, country: value }))}
                      data={[
                        { value: 'US', label: 'United States' },
                        { value: 'CA', label: 'Canada' },
                        { value: 'MX', label: 'Mexico' },
                        { value: 'UK', label: 'United Kingdom' },
                        { value: 'AU', label: 'Australia' },
                        { value: 'OTHER', label: 'Other' }
                      ]}
                      style={{ width: '150px' }}
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Phone</Text>
                    <TextInput
                      value={guestForm.phone}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, phone: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </Group>

                  {/* Row 6: Phone1, Phone2 */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Phone1</Text>
                    <TextInput
                      value={guestForm.phone1}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, phone1: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Phone2</Text>
                    <TextInput
                      value={guestForm.phone2}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, phone2: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </Group>

                  {/* Row 7: Email */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Email</Text>
                    <TextInput
                      type="email"
                      value={guestForm.email}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, email: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </Group>
            </Stack>
              </Grid.Col>

              {/* Right Half */}
              <Grid.Col span={6}>
                <Stack gap="md">
                  {/* Row 1: Market, Source */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Market</Text>
                    <Select
                      value={guestForm.market}
                      onChange={(value) => setGuestForm(prev => ({ ...prev, market: value }))}
                      data={[
                        { value: 'LEISURE', label: 'Leisure' },
                        { value: 'BUSINESS', label: 'Business' },
                        { value: 'GROUP', label: 'Group' },
                        { value: 'CORPORATE', label: 'Corporate' },
                        { value: 'WHOLESALE', label: 'Wholesale' }
                      ]}
                      placeholder="Select market"
                      style={{ flex: 1 }}
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Source</Text>
                    <Select
                      value={guestForm.source}
                      onChange={(value) => setGuestForm(prev => ({ ...prev, source: value }))}
                      data={[
                        { value: 'DIRECT', label: 'Direct' },
                        { value: 'BOOKING_ENGINE', label: 'Booking Engine' },
                        { value: 'PHONE', label: 'Phone' },
                        { value: 'WALK_IN', label: 'Walk-in' },
                        { value: 'TRAVEL_AGENT', label: 'Travel Agent' },
                        { value: 'GROUP_SALES', label: 'Group Sales' }
                      ]}
                      placeholder="Select source"
                      style={{ flex: 1 }}
                    />
                  </Group>

                  {/* Credit Card Fields */}
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Credit Card #</Text>
                    <TextInput
                      value={guestForm.creditCardNumber}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, creditCardNumber: e.target.value }))}
                      placeholder="1234 5678 9012 3456"
                      style={{ flex: 1 }}
                    />
                  </Group>

                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '80px' }}>Expiration</Text>
                    <TextInput
                      value={guestForm.expirationDate}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                      placeholder="MM/YY"
                      style={{ width: '100px' }}
                    />
                    <Text size="sm" fw={500} style={{ minWidth: '60px' }}>CSV</Text>
                    <TextInput
                      value={guestForm.csv}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, csv: e.target.value }))}
                      placeholder="123"
                      style={{ width: '80px' }}
                    />
                  </Group>

                  {/* Notes - Multi-line */}
                  <Group align="flex-start">
                    <Text size="sm" fw={500} style={{ minWidth: '80px', marginTop: '8px' }}>Notes</Text>
                    <TextInput
                      value={guestForm.notes}
                      onChange={(e) => setGuestForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes about the guest or reservation"
                      style={{ flex: 1 }}
                      multiline
                      rows={6}
                      autosize
                      minRows={4}
                      maxRows={8}
                    />
                  </Group>
                </Stack>
              </Grid.Col>
            </Grid>
        </Paper>

          {/* Selected Quote Display */}
          {rateQuotes.length > 0 && (
            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="center">
                <Text size="sm">
                  {rateQuotes[0].description} Rate {rateQuotes[0].name}
                </Text>
                <Group gap="md">
                  <Text size="sm">Rate ${rateQuotes[0].baseRate.toFixed(2)}</Text>
                  <Text size="sm">Taxes ${rateQuotes[0].taxes.toFixed(2)}</Text>
                  <Text size="sm">Other ${rateQuotes[0].otherCharges.toFixed(2)}</Text>
                  <Text size="sm">Security ${rateQuotes[0].securityDeposit.toFixed(2)}</Text>
                  <Text size="sm" fw={700} c="blue">Total ${rateQuotes[0].total.toFixed(2)}</Text>
                </Group>
              </Group>
            </Paper>
          )}
        </Stack>
      ) : (
        /* Confirmation Screen */
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Title order={1}>Reservation Confirmation</Title>
            <Badge color="green" size="lg">
              CONFIRMED
            </Badge>
          </Group>

          {/* Success Message */}
          <Alert color="green" title="Reservation Created Successfully">
            Your reservation has been created and confirmed. Please review the details below.
          </Alert>

          {/* Reservation Details */}
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Title order={3} mb="md">Reservation Details</Title>
            <Grid>
              <Grid.Col span={6}>
                <Stack gap="md">
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Reservation Number:</Text>
                    <Text size="lg" fw={700} c="blue">{createdReservation?.id}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Guest Name:</Text>
                    <Text size="md">{guestForm.firstName} {guestForm.lastName}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Arrival Date:</Text>
                    <Text size="md">{arrivalDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Nights:</Text>
                    <Text size="md">{nights}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Departure Date:</Text>
                    <Text size="md">{departureDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</Text>
                  </Group>
                </Stack>
              </Grid.Col>
              
              <Grid.Col span={6}>
                <Stack gap="md">
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Unit Type:</Text>
                    <Text size="md">{unitTypes.find(ut => ut.value === selectedUnitType)?.label || 'N/A'}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Unit Number:</Text>
                    <Text size="md">{selectedUnit?.unit_number || 'N/A'}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Rate Plan:</Text>
                    <Text size="md">{selectedRate?.title || 'N/A'}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Guests:</Text>
                    <Text size="md">{adults} Adult{adults !== 1 ? 's' : ''}{children > 0 ? `, ${children} Child${children !== 1 ? 'ren' : ''}` : ''}</Text>
                  </Group>
                  
                  <Group>
                    <Text size="sm" fw={500} style={{ minWidth: '120px' }}>Status:</Text>
                    <Badge color="green" size="md">CONFIRMED</Badge>
                  </Group>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Pricing Summary */}
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Title order={3} mb="md">Pricing Summary</Title>
            <Grid>
              <Grid.Col span={6}>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm">Base Rate:</Text>
                    <Text size="sm" fw={500}>${rateSummary.baseRate.toFixed(2)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Taxes ({taxConfig ? calculateTaxPercentage(taxConfig) : 0}%):</Text>
                    <Text size="sm" fw={500}>${rateSummary.taxes.toFixed(2)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Other Charges:</Text>
                    <Text size="sm" fw={500}>${rateSummary.otherCharges.toFixed(2)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Security Deposit:</Text>
                    <Text size="sm" fw={500}>${rateSummary.securityDeposit.toFixed(2)}</Text>
                  </Group>
                </Stack>
              </Grid.Col>
              <Grid.Col span={6}>
                <Divider />
                <Group justify="space-between" mt="md">
                  <Text size="lg" fw={700}>Total Amount:</Text>
                  <Text size="xl" fw={700} c="blue">${rateSummary.total.toFixed(2)}</Text>
                </Group>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Action Buttons */}
          <Group justify="center" mt="xl">
            <Button
              size="lg"
              onClick={handleFinalConfirmation}
              leftSection={<IconCheck size="1rem" />}
            >
              Back to Front Desk
            </Button>
          </Group>
        </Stack>
      )}

      {/* Search Modal */}
      <Modal
        opened={searchModalOpen}
        onClose={() => {
          setSearchModalOpen(false);
          setSearchTerm('');
          setSearchResults([]);
        }}
        title={`Search ${searchType === 'guest' ? 'Guests' : searchType === 'group' ? 'Groups' : 'Travel Agents'}`}
        size="lg"
      >
        <Stack gap="md">
          <Group>
            <TextInput
              placeholder={`Search ${searchType === 'guest' ? 'by name, email, or guest number' : searchType === 'group' ? 'by group name or contact' : 'by agent name or agency'}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftSection={<IconSearch size="1rem" />}
              style={{ flex: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              onClick={handleSearch}
              loading={searchLoading}
              leftSection={<IconSearch size="1rem" />}
            >
              Search
            </Button>
          </Group>

          {searchResults.length > 0 && (
            <ScrollArea.Autosize mah={400}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    {searchType === 'guest' && (
                      <>
                        <Table.Th>Guest #</Table.Th>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Phone</Table.Th>
                        <Table.Th>Type</Table.Th>
                      </>
                    )}
                    {searchType === 'group' && (
                      <>
                        <Table.Th>Group Name</Table.Th>
                        <Table.Th>Contact</Table.Th>
                        <Table.Th>Phone</Table.Th>
                        <Table.Th>Email</Table.Th>
                      </>
                    )}
                    {searchType === 'agent' && (
                      <>
                        <Table.Th>Agent Name</Table.Th>
                        <Table.Th>Agency</Table.Th>
                        <Table.Th>Phone</Table.Th>
                        <Table.Th>Email</Table.Th>
                      </>
                    )}
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {searchResults.map((result) => (
                    <Table.Tr key={result.id}>
                      {searchType === 'guest' && (
                        <>
                          <Table.Td>{result.guestnumber || result.id}</Table.Td>
                          <Table.Td>{`${result.firstname} ${result.lastname}`}</Table.Td>
                          <Table.Td>{result.email}</Table.Td>
                          <Table.Td>{result.phone1}</Table.Td>
                          <Table.Td>{result.guesttype}</Table.Td>
                        </>
                      )}
                      {searchType === 'group' && (
                        <>
                          <Table.Td>{result.name}</Table.Td>
                          <Table.Td>{result.contact}</Table.Td>
                          <Table.Td>{result.phone}</Table.Td>
                          <Table.Td>{result.email}</Table.Td>
                        </>
                      )}
                      {searchType === 'agent' && (
                        <>
                          <Table.Td>{`${result.firstname} ${result.lastname}`}</Table.Td>
                          <Table.Td>{result.agency_name}</Table.Td>
                          <Table.Td>{result.phone1}</Table.Td>
                          <Table.Td>{result.email}</Table.Td>
                        </>
                      )}
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="green"
                          onClick={() => handleSelectAccount(result)}
                        >
                          <IconCheck size="1rem" />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          )}

          {searchResults.length === 0 && searchTerm && !searchLoading && (
            <Text c="dimmed" ta="center" py="xl">
              No {searchType}s found matching "{searchTerm}"
            </Text>
          )}

          {!searchTerm && (
            <Text c="dimmed" ta="center" py="xl">
              Enter a search term to find {searchType}s
            </Text>
          )}
        </Stack>
      </Modal>

      {/* Unit Selection Modal */}
      <Modal
        opened={unitModalOpen}
        onClose={() => setUnitModalOpen(false)}
        title="Select Available Unit"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Available units for {arrivalDate?.toLocaleDateString()} - {departureDate?.toLocaleDateString()}
            {selectedUnitType && ` (${unitTypes.find(ut => ut.value === selectedUnitType)?.label})`}
          </Text>

          {unitsLoading ? (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          ) : availableUnits.length > 0 ? (
            <ScrollArea.Autosize maxHeight={400}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Unit Number</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Address</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {availableUnits.map((unit) => (
                    <Table.Tr key={unit.id}>
                      <Table.Td>
                        <Text fw={500}>{unit.unit_number}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{unit.unit_type_name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{unit.address}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="green" size="sm">
                          {unit.clean_status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => handleSelectUnit(unit)}
                        >
                          Select
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          ) : (
            <Alert color="orange" title="No Available Units">
              No units are available for the selected dates and criteria.
            </Alert>
          )}
        </Stack>
      </Modal>

      {/* Rate Plan Selection Modal */}
      <Modal
        opened={rateModalOpen}
        onClose={() => setRateModalOpen(false)}
        title="Select Rate Plan"
        size="90%"
        styles={{
          content: {
            maxWidth: '95vw',
            height: '90vh',
            maxHeight: '90vh'
          },
          body: {
            height: 'calc(90vh - 120px)',
            overflow: 'auto'
          }
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select from existing rate plans. To create or manage rate plans, go to Setup → Rates → Rate Management.
          </Text>

          {ratesLoading ? (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          ) : ratePlans.length > 0 ? (
            <Box>
              {/* Rate Grids for All Active Rates */}
              {ratePlans.filter(rate => rate.active).map((ratePlan) => (
                <Box key={ratePlan.id} mb="xl">
                  <Group justify="space-between" mb="md">
                    <Box>
                      <Text fw={500} size="lg">
                        {ratePlan.title}
                      </Text>
                      {rateGrids[ratePlan.id] && rateGrids[ratePlan.id].length > 0 && (
                        <Group gap="lg" mt="xs">
                          <Text size="sm" c="dimmed">
                            Total for {nights} nights: ${rateGrids[ratePlan.id].reduce((sum, day) => sum + day.rate, 0).toFixed(2)}
                          </Text>
                          <Text size="sm" c="dimmed">
                            Average per night: ${(rateGrids[ratePlan.id].reduce((sum, day) => sum + day.rate, 0) / nights).toFixed(2)}
                          </Text>
                        </Group>
                      )}
                    </Box>
                    <Button
                      size="sm"
                      variant="filled"
                      onClick={() => handleSelectRate(ratePlan)}
                    >
                      Select This Rate
                    </Button>
                  </Group>

                  {rateGrids[ratePlan.id] && rateGrids[ratePlan.id].length > 0 ? (
                    <Box>
                      <ScrollArea.Autosize maxHeight={200}>
                        <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                              <Table.Th>Date</Table.Th>
                              {rateGrids[ratePlan.id].map((day, index) => (
                                <Table.Th key={index} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {day.formattedDate}
                                </Table.Th>
                              ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                            <Table.Tr>
                              <Table.Td fw={500}>Rate</Table.Td>
                              {rateGrids[ratePlan.id].map((day, index) => (
                                <Table.Td key={index} style={{ textAlign: 'center' }}>
                                  <Text fw={500} c="blue">
                                    ${day.rate.toFixed(2)}
                                  </Text>
                      </Table.Td>
                              ))}
                            </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
                    </Box>
                  ) : (
                    <Alert color="yellow" title="Loading Rate Data">
                      Loading rate information for {ratePlan.title}...
                    </Alert>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Alert color="orange" title="No Rate Plans Found">
              No rate plans are available. Please create rate plans in Setup → Rates → Rate Management.
            </Alert>
          )}
        </Stack>
      </Modal>
    </Container>
  );
}

export default ReservationScreen;
